import { useCallback, useEffect, useState } from 'react';

import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import { Bars } from '@/components/mic-selector/Bars';
import { useAudioGraphStore } from '@/lib/audio/useAudioGraphStore';
import { logger } from '@/utils/logger/logger';
import { useAudioDevices, useAudioSystem } from '@heidi/capture';
import {
  Banner,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@heidi/design-system/select';
import { TypographyH2, TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { ChevronDown, Mic } from 'lucide-react';

type MicPermissionState = 'prompt' | 'granted' | 'denied';

interface MicPermissionsStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

export function MicPermissionsStep({ onContinue, onSkip }: MicPermissionsStepProps) {
  const { t } = useTranslations();
  const [permissionState, setPermissionState] = useState<MicPermissionState>('prompt');

  const currentDeviceLabel = useAudioGraphStore((state) => state.device?.label);
  const { deviceId, changeDeviceId, availableDevices } = useAudioDevices();
  const { start: startAudioSystem, isReady: isAudioReady } = useAudioSystem();
  const setReadyToRequestMic = useSetAtom(readyToRequestMicPermissionsAtom);

  useEffect(() => {
    const applyStatus = (state: PermissionState) => {
      if (state === 'granted') {
        setPermissionState('granted');
      } else if (state === 'denied') {
        setPermissionState('denied');
      } else {
        setPermissionState('prompt');
      }
    };

    void navigator.permissions
      ?.query({ name: 'microphone' as PermissionName })
      .then((status) => {
        applyStatus(status.state);
        status.onchange = () => applyStatus(status.state);
      })
      .catch(() => {
        // permissions API not supported — fall through to manual request
      });
  }, []);

  useEffect(() => {
    if (permissionState === 'granted') {
      setReadyToRequestMic(true);
      if (!isAudioReady) {
        // start() can reject on mic acquisition failure (e.g. device in
        // use); this boot is best-effort, so log rather than surface here.
        startAudioSystem().catch((err) => {
          logger.error({
            event_type: 'ONBOARDING_START_AUDIO_SYSTEM_FAILED',
            details: { error: err instanceof Error ? err.message : String(err) },
          });
        });
      }
    }
  }, [permissionState, isAudioReady, startAudioSystem, setReadyToRequestMic]);

  const handleAllowMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionState('granted');
    } catch {
      setPermissionState('denied');
    }
  }, []);

  const isGranted = permissionState === 'granted';
  const isDenied = permissionState === 'denied';

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TypographyH2>{t('signup.micTitle')}</TypographyH2>
      </div>

      <TypographyP>{t('signup.micSubtitle')}</TypographyP>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          {permissionState === 'prompt' && (
            <Button
              size="lg"
              type="button"
              className="w-fit"
              onClick={() => void handleAllowMicrophone()}
            >
              <Mic size={16} />
              <TypographyP2 className="font-medium text-white">
                {t('signup.micAllow')}
              </TypographyP2>
            </Button>
          )}

          {isDenied && (
            <Banner subVariant="warning">
              <BannerContent>
                <BannerTitle>{t('signup.micBlockedTitle')}</BannerTitle>
                <BannerDescription>{t('signup.micBlockedDescription')}</BannerDescription>
              </BannerContent>
            </Banner>
          )}

          {isGranted && (
            <Select value={deviceId ?? ''} onValueChange={(d) => void changeDeviceId(d)}>
              <SelectTrigger
                className="size-auto border-transparent bg-transparent p-0"
                hideSelectIcon
              >
                <div className="flex w-full items-center gap-2 rounded-lg border border-border bg-white py-3 pl-3 pr-2">
                  <TypographyP2 className="flex-1 truncate text-left text-text-secondary">
                    {currentDeviceLabel ?? ''}
                  </TypographyP2>
                  <div className="flex items-center gap-0.5">
                    <Bars isMic isTab={false} />
                    <ChevronDown size={16} className="text-text-secondary" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent align="start" side="bottom">
                {isEmpty(availableDevices) && (
                  <TypographyP2 className="max-w-[200px] items-center p-4 text-center text-text-secondary">
                    {t('recording.noInputDevices')}
                  </TypographyP2>
                )}
                <SelectGroup>
                  {availableDevices.map((device, index) => (
                    <SelectItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="flex flex-1 items-end pt-4">
        <div className="flex w-full flex-col gap-2">
          <Button
            size="lg"
            type="button"
            onClick={onContinue}
            disabled={!isGranted}
            className="w-full"
          >
            {t('common.continue')}
          </Button>
          <Button
            size="lg"
            type="button"
            variant="ghost"
            onClick={onSkip}
            disabled={isGranted}
            className="w-full"
          >
            {t('signup.skip')}
          </Button>
        </div>
      </div>
    </div>
  );
}
